/**
 * JWT-redacting logger.
 *
 * PRD-002 DoD: no log message — application, infrastructure, request traces,
 * error stacks, third-party telemetry — ever contains the JWT or any
 * substring sufficient to reconstruct one.
 *
 * This module is the only sanctioned way to log within the auth namespace.
 * Static analysis (`tools/jwt-log-ast-scan.ts`) ensures no `console.*` call
 * passes auth-namespaced data without going through `redact`.
 */

const SENSITIVE_KEYS = /^(authorization|auth|token|jwt|bearer|access[_-]?token|refresh[_-]?token)$/i;

/** Rough JWT shape match — three base64-url-ish segments separated by dots. */
const JWT_SHAPE = /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\b/g;

export function redact(value: unknown): unknown {
    if (value === null || value === undefined) return value;
    if (typeof value === "string") {
        return value.replace(JWT_SHAPE, "[REDACTED-JWT]");
    }
    if (typeof value !== "object") return value;
    if (Array.isArray(value)) {
        return value.map(redact);
    }
    if (value instanceof Error) {
        return {
            name: value.name,
            message:
                typeof value.message === "string" ? value.message.replace(JWT_SHAPE, "[REDACTED-JWT]") : value.message,
        };
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
        out[k] = SENSITIVE_KEYS.test(k) ? "[REDACTED]" : redact(v);
    }
    return out;
}

export const logger = {
    info(...args: unknown[]): void {
        console.info(...args.map(redact));
    },
    warn(...args: unknown[]): void {
        console.warn(...args.map(redact));
    },
    error(...args: unknown[]): void {
        console.error(...args.map(redact));
    },
};
